"use client";

import { useEffect, useRef, useState } from "react";
import styles from "./DeskTutorChicky.module.css";

interface DeskTutorChickyProps {
  isVisible: boolean;
  reducedMotion: boolean;
  isAuthenticated?: boolean;
}

const SCENE_WIDTH = 1280;
const SCENE_HEIGHT = 720;
const DESK_ANCHOR_X = 200;
const DESK_ANCHOR_Y = 635;
const SOURCE_CHARACTER_WIDTH = 88;

export function DeskTutorChicky({
  isVisible,
  reducedMotion,
  isAuthenticated = false,
}: DeskTutorChickyProps) {
  const sceneRef = useRef<HTMLDivElement>(null);
  const characterRef = useRef<HTMLButtonElement>(null);
  const deskOcclusionRef = useRef<HTMLDivElement>(null);
  const replyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isReplyVisible, setIsReplyVisible] = useState(false);

  const replyContent = isAuthenticated ? (
    <span>
      <span className="block">No easter eggs here.</span>{" "}
      <span className="block">Ready to practice?</span>
    </span>
  ) : (
    <span>
      <span className="block">Ready to practice?</span>{" "}
      <span className="block">Pick a language and talk to me</span>
    </span>
  );

  const showReply = () => {
    if (replyTimerRef.current) clearTimeout(replyTimerRef.current);
    setIsReplyVisible(true);
    replyTimerRef.current = setTimeout(() => {
      setIsReplyVisible(false);
      replyTimerRef.current = null;
    }, 3500);
  };

  useEffect(
    () => () => {
      if (replyTimerRef.current) clearTimeout(replyTimerRef.current);
    },
    []
  );

  useEffect(() => {
    const scene = sceneRef.current;
    const character = characterRef.current;
    const deskOcclusion = deskOcclusionRef.current;
    if (!scene || !character || !deskOcclusion) return;

    const positionCharacter = () => {
      const rect = scene.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const mediaScale = Math.max(rect.width / SCENE_WIDTH, rect.height / SCENE_HEIGHT);
      const renderedWidth = SCENE_WIDTH * mediaScale;
      const renderedHeight = SCENE_HEIGHT * mediaScale;
      const offsetX = (rect.width - renderedWidth) / 2;
      const offsetY = (rect.height - renderedHeight) / 2;
      const characterWidth = Math.min(132, Math.max(82, SOURCE_CHARACTER_WIDTH * mediaScale));
      const anchorX = offsetX + DESK_ANCHOR_X * mediaScale;
      const anchorY = offsetY + DESK_ANCHOR_Y * mediaScale;
      const occlusionWidth = characterWidth * 1.18;
      const occlusionHeight = Math.min(14, Math.max(9, 7 * mediaScale));
      const occlusionLeft = anchorX - occlusionWidth / 2;
      const occlusionTop = anchorY - occlusionHeight * 0.34;

      character.style.left = `${anchorX}px`;
      character.style.top = `${anchorY}px`;
      character.style.width = `${characterWidth}px`;
      character.dataset.positioned = "true";

      deskOcclusion.style.left = `${occlusionLeft}px`;
      deskOcclusion.style.top = `${occlusionTop}px`;
      deskOcclusion.style.width = `${occlusionWidth}px`;
      deskOcclusion.style.height = `${occlusionHeight}px`;
      deskOcclusion.style.backgroundSize = `${renderedWidth}px ${renderedHeight}px`;
      deskOcclusion.style.backgroundPosition = `${offsetX - occlusionLeft}px ${
        offsetY - occlusionTop
      }px`;
      deskOcclusion.dataset.positioned = "true";
    };

    positionCharacter();

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(positionCharacter);
    observer.observe(scene);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={sceneRef}
      className={styles.scene}
      data-testid="desk-tutor-chicky"
      data-visible={isVisible ? "true" : "false"}
      data-motion={reducedMotion ? "static" : "animated"}
    >
      <button
        ref={characterRef}
        type="button"
        className={styles.character}
        aria-label="Ask Chicky about Easter eggs"
        aria-describedby={isReplyVisible ? "desk-tutor-chicky-reply" : undefined}
        disabled={!isVisible}
        onClick={showReply}
      >
        {isReplyVisible && (
          <span id="desk-tutor-chicky-reply" className={styles.reply} role="status">
            {replyContent}
          </span>
        )}
        <span className={styles.frame} aria-hidden="true">
          <img
            className={reducedMotion ? styles.staticStrip : styles.animatedStrip}
            src="/ui/brand-kit/mascot/chicky-desk-teaching-strip.webp?v=codex-review-r2"
            alt=""
            width={1152}
            height={208}
            loading="lazy"
            decoding="async"
            draggable={false}
          />
        </span>
      </button>
      <div
        ref={deskOcclusionRef}
        className={styles.deskOcclusion}
        data-testid="desk-tutor-occlusion"
        aria-hidden="true"
      />
    </div>
  );
}
